import React, { useMemo, useState } from 'react';
import { SelectPicker } from 'rsuite';

const uniqueSorted = (values) =>
  [...new Set(values.filter((v) => v !== null && v !== undefined && v !== ''))].sort((a, b) =>
    String(a).localeCompare(String(b))
  );

const toTaskPair = (row, taskField) => {
  const task = String(row?.[taskField] || '').trim();
  const parent = String(row?.parentTaskName || '').trim();
  return {
    key: `${task}__parent__${parent}`,
    label: parent ? `${task} (Parent: ${parent})` : task,
  };
};

// Filter state + derived rows for a table. Filters by an exact task and bucket
// value plus a free-text search across `searchFields`.
export function useRowFilter(rows, { taskField = 'taskName', bucketField = 'bucket', searchFields = [] } = {}) {
  const [search, setSearch] = useState('');
  const [taskFilter, setTaskFilter] = useState(null);
  const [bucketFilter, setBucketFilter] = useState(null);

  const taskOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const pair = toTaskPair(row, taskField);
      if (!pair.label) return;
      map.set(pair.key, { label: pair.label, value: pair.key });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, taskField]);
  const bucketOptions = useMemo(
    () => uniqueSorted(rows.map((r) => r[bucketField])).map((v) => ({ label: v, value: v })),
    [rows, bucketField]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (taskFilter && toTaskPair(r, taskField).key !== taskFilter) return false;
      if (bucketFilter && r[bucketField] !== bucketFilter) return false;
      if (q && !searchFields.some((f) => String(r[f] ?? '').toLowerCase().includes(q))) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, taskFilter, bucketFilter, taskField, bucketField]);

  return {
    filtered, search, setSearch, taskFilter, setTaskFilter, bucketFilter, setBucketFilter, taskOptions, bucketOptions,
  };
}

// Compare two cell values for sorting. Empty/null sort last; numeric columns
// compare numerically, everything else by locale string order.
const compareCells = (a, b) => {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const an = Number(a);
  const bn = Number(b);
  const bothNumeric = Number.isFinite(an) && Number.isFinite(bn)
    && String(a).trim() !== '' && String(b).trim() !== '';
  if (bothNumeric) return an - bn;
  return String(a).localeCompare(String(b));
};

// Header-click sorting for a plain HTML table. `sortKey` selects the field to
// sort by (defaults to the column key). Returns the sorted rows plus the state
// and handler the <SortHeader> cells need.
export function useSortedRows(rows, { column = null, type = 'asc' } = {}) {
  const [sortColumn, setSortColumn] = useState(column);
  const [sortType, setSortType] = useState(type);

  const onSort = (col) => {
    if (col === sortColumn) {
      setSortType((t) => (t === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      setSortType('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortColumn) return rows;
    const dir = sortType === 'asc' ? 1 : -1;
    return [...rows].sort((r1, r2) => dir * compareCells(r1[sortColumn], r2[sortColumn]));
  }, [rows, sortColumn, sortType]);

  return { sorted, sortColumn, sortType, onSort };
}

// A clickable <th> that drives useSortedRows. `column` is the sort field.
export function SortHeader({ sort, column, children, align, style }) {
  const active = sort.sortColumn === column;
  const icon = active ? (sort.sortType === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up';
  return (
    <th
      onClick={() => sort.onSort(column)}
      className={align === 'end' ? 'text-end' : undefined}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', ...style }}
      title="Click to sort"
    >
      {children} <i className={`bi ${icon}`} style={{ fontSize: 10, opacity: active ? 0.9 : 0.3 }} />
    </th>
  );
}

export function RowFilterBar({ filter, searchPlaceholder = 'Search…', total, children }) {
  const { search, setSearch, taskFilter, setTaskFilter, bucketFilter, setBucketFilter, taskOptions, bucketOptions, filtered } = filter;
  const active = search || taskFilter || bucketFilter;
  return (
    <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
      <input
        type="search"
        className="form-control form-control-sm"
        style={{ maxWidth: 240 }}
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <SelectPicker size="sm" data={taskOptions} value={taskFilter} onChange={setTaskFilter} placeholder="All tasks" style={{ width: 200 }} />
      <SelectPicker size="sm" data={bucketOptions} value={bucketFilter} onChange={setBucketFilter} placeholder="All buckets" searchable={false} style={{ width: 190 }} />
      {children}
      {active ? (
        <span className="text-muted small">
          {filtered.length}{total != null ? ` of ${total}` : ''} shown
        </span>
      ) : null}
    </div>
  );
}
